import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Breadcrumbs, { breadcrumbsClasses } from '@mui/material/Breadcrumbs';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';

// Styles for the project
const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  [`& .${breadcrumbsClasses.separator}`]: {
    color: theme.palette.action.disabled,
    margin: 1,
  },
  [`& .${breadcrumbsClasses.ol}`]: {
    alignItems: 'center',
  },
}));

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export default function HeaderPage({
  actions,
  breadcrumbs,
  description,
  title,
  variant = 'breadcrumb',
}) {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(Boolean);

  const breadcrumbItems = Array.isArray(breadcrumbs) && breadcrumbs.length
    ? breadcrumbs
    : pathnames.map((segment) => ({
        label: capitalize(decodeURIComponent(segment)),
      }));

  if (variant === 'listing') {
    const fallbackTitle = breadcrumbItems[breadcrumbItems.length - 1]?.label || title;

    return (
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                alignItems: 'center',
                color: '#111827',
                display: 'flex',
                fontSize: { xs: 20, sm: 22 },
                fontWeight: 700,
                gap: 1,
                lineHeight: 1.2,
                '&::before': {
                  backgroundColor: '#5f48f6',
                  borderRadius: '999px',
                  content: '""',
                  display: 'inline-block',
                  height: 18,
                  width: 3,
                },
              }}
            >
              {title || fallbackTitle}
            </Typography>

            {description ? (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: 14,
                  lineHeight: 1.45,
                  mt: 0.75,
                  maxWidth: 640,
                }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>

          {actions ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexShrink: 0,
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              {actions}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <StyledBreadcrumbs
      aria-label="breadcrumb"
      separator={<NavigateNextRoundedIcon fontSize="small" />}
      sx={{ paddingTop: '12px', marginLeft: '30px' }}
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const label = typeof item === 'string' ? item : item?.label;

        return (
          <Typography
            key={index}
            variant="body1"
            sx={{
              color: isLast ? 'text.primary' : 'inherit',
              fontWeight: isLast ? 600 : undefined,
            }}
          >
            {label}
          </Typography>
        );
      })}
    </StyledBreadcrumbs>
  );
}
